/**
 * ProjectPage - Unified project wrapper using optimized DisciplineMatrix
 * 
 * This component replaces the legacy ProjectDetail.js by:
 * - Using DisciplineMatrix for the piping matrix (optimized with useReducer + react-virtualized)
 * - Preserving all non-matrix features (Material Log, HVAC, MTO, etc.)
 * - Managing all modals at this level
 * - Handling menu routing
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useResponsive } from '../../hooks/useResponsive';

// Optimized matrix component
import DisciplineMatrix from '../Matrix/DisciplineMatrix';
import DisciplineColumnConfigModal from '../Matrix/DisciplineColumnConfigModal';
import { getSubTableById, MECHANICAL_SUB_TABLES } from '../Matrix/MechanicalTableDropdown';
import { getDisciplineConfig } from '../../config/disciplines';
import { getMenuItemLabel } from '../../config/menuStructure';
import { canAccessResource } from '../../config/resourceAccess';
import { canSeeMenuItem, isExternalEmail } from '../../utils/menuAccess';

// Layout components
import ProjectSidebar from '../Layout/ProjectSidebar';
import Toast from '../Layout/Toast';
import { useToast } from '../../hooks/useToast';

// Job monitoring
import JobStatusMonitor from '../ProgressTracking/Piping/JobStatusMonitor';

// Piping-specific modals
import UploadMatrixModal from '../ProgressTracking/Piping/UploadMatrixModal';
import ProgressTrendsModal from '../ProgressTracking/Piping/ProgressTrendsModal';
// ColumnConfigModal removed - piping now uses DisciplineColumnConfigModal like other disciplines
import ExportOptionsModal from '../ProgressTracking/Piping/ExportOptionsModal';
import MatBorePercentagesModal from '../ProgressTracking/Piping/Admin/MatBorePercentagesModal';
import ContractorSelectionCriteriaModal from '../Procurement/ContractorSelectionCriteriaModal';
// SelectedContractors is now a standalone route at /contractors (not project-specific)
// ContactUs is now accessed via header Help dropdown (ContactModal)
import DailyReport from '../Technical/DailyReport';
import SheetsDashboard from '../Technical/Sheets/SheetsDashboard';
import DownloadApp from '../DownloadApp';
import PMORadiator from '../PMO/PMORadiator';
import PortalUsageDashboard from '../PMO/PortalUsageDashboard';
import SubcontractorDiscovery from '../PMO/SubcontractorDiscovery';
import usePortalUsageTracker from '../../hooks/usePortalUsageTracker';

// Non-matrix components
import MaterialLog from '../MaterialManagement/MaterialLog';
import ActualSpentLog from '../Actuals/ActualSpentLog';
import EarnedReport from '../Reports/EarnedReport';
import EVMReport from '../Reports/EVMReport';
import ClientReport from '../Reports/ClientReport';
import BudgetReport from '../Reports/BudgetReport';
import TariffLog from '../MaterialManagement/TariffLog';
import PurchasingListLog from '../MaterialManagement/PurchasingListLog';
import HvacTracking from '../ProgressTracking/HvacTracking';
import RotatingEquipmentTracking from '../ProgressTracking/RotatingEquipmentTracking';
import DuctsTracking from '../ProgressTracking/DuctsTracking';
import VesselsTracking from '../ProgressTracking/VesselsTracking';
import StructuralSteelTracking from '../ProgressTracking/StructuralSteelTracking';
import ChangeOrdersTracking from '../ProgressTracking/ChangeOrdersTracking';
import CCRUploadModal from '../ProgressTracking/CCRUploadModal';
import CCRExportOptionsModal from '../ProgressTracking/CCRExportOptionsModal';
import MechanicalUploadModal from '../ProgressTracking/Mechanical/MechanicalUploadModal';
import ExportModal from '../common/ExportModal';
import UploadModal from '../common/UploadModal';
import { EXPORT_CONFIGS } from '../../config/exportConfigs';
import { UPLOAD_CONFIGS } from '../../config/uploadConfigs';
import MtoLog from '../Estimation/MtoLog';
import MaterialIndexLog from '../Estimation/MaterialIndexLog';
import BomExtractionPage from '../Estimation/BomExtraction/BomExtractionPage';
import BomExtractorApiPage from '../Estimation/BomExtractorApiPage';
import BomExtractorApi2Page from '../Estimation/BomExtractorApi2Page';
import { ForecastingPlaceholder } from '../Forecasting';
import ForecastingDashboard1 from '../Forecasting/ForecastingDashboard1';
import MaestroReports from '../Forecasting/MaestroReports';
import BillingDashboard from '../Forecasting/BillingDashboard';
import TransmittalLog from '../Documentation/TransmittalLog';
import SharePointBookmarksPage from '../Documentation/SharePointBookmarksPage';
import RentalsLog from '../Estimation/RentalsLog';
import LabourRatesLog from '../Estimation/LabourRatesLog';
import RulesOfCreditModal from '../common/RulesOfCreditModal';
import DevelopmentLog from '../Development/DevelopmentLog';
import CodeRefactoringDashboard from '../Development/CodeRefactoringDashboard';
import FileUploadPage from '../Development/FileUploadPage';
import TestPipingProgressTracker from '../Test/TestPipingProgressTracker';
import PowerBIReport from '../Dashboard/PowerBIReport';
import HSEReport from '../Reports/HSEReport';
import SOVReport from '../Reports/SOVReport';
import ProcessPdfViewer from '../Processes/ProcessPdfViewer';
import ProjectUserAssignmentModal from '../Admin/ProjectUserAssignmentModal';
import CurrencyExchange from '../MaterialManagement/CurrencyExchange';
import Tutorial from '../Tutorial/Tutorial';
import PhotosLog from '../TechnicalPhotos/PhotosLog';
import SpecificationsDashboard from '../Technical/Specifications/SpecificationsDashboard';
import MeetingsDashboard from '../Technical/Meetings/MeetingsDashboard';
import IssuesDashboard from '../Technical/Issues/IssuesDashboard';
import SubmittalsDashboard from '../Technical/Submittals/SubmittalsDashboard';
import ChecklistsDashboard from '../Technical/Checklists/ChecklistsDashboard';
import AssetsDashboard from '../Technical/Assets/AssetsDashboard';
import FormsDashboard from '../Technical/Forms/FormsDashboard';
import RFILogPage from '../Technical/RFILogPage';
import RequisitionLogPage from '../Technical/Requisitions/RequisitionLogPage';
import KeyboardShortcuts from '../Training/KeyboardShortcuts';
import MouseGestures from '../Training/MouseGestures';
import { ScheduleInstances, Planner, MasterComponentList, GaDashboards, ScheduleModel, ScheduleChecklists, EstimateLbsOnePager, ScheduleModulesPanel, ExecutionPlan, ScheduleAnalysis, MspExport } from '../Schedule';
import QAQCForms from '../QAQC/QAQCForms';
import FillableForm from '../QAQC/FillableForm';
import ControlChartDashboard from '../ChangeManagement/ControlChartDashboard';
import WelderLog from '../ProgressTracking/Piping/WelderLog';
import NdeImportModal from '../ProgressTracking/Piping/NdeImportModal';
import NdeAuditLogModal from '../ProgressTracking/Piping/NdeAuditLogModal';
import PipeTestingImportModal from '../ProgressTracking/Piping/PipeTestingImportModal';
import PipeTestingAuditLogModal from '../ProgressTracking/Piping/PipeTestingAuditLogModal';
import * as portalSession from '../../auth/portalSession';

const ProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  // Signed in via Microsoft (MSAL account) or via e-mail code (portal session, external users)
  const hasSession = (accounts && accounts.length > 0) || portalSession.isActive();
  const { isMobile, isTablet } = useResponsive();

  // ============================================
  // Core State
  // ============================================
  const signedInEmail = accounts?.[0]?.username || portalSession.current()?.user?.email || '';
  const guest = isExternalEmail(signedInEmail);

  const getInitialMenuItem = () => {
    const hash = window.location.hash.replace('#', '');
    let saved = '';
    try { saved = localStorage.getItem(`project_${projectId}_activeMenu`) || ''; } catch { /* ignore */ }
    // Guests land on Progress Tracking. A saved GA board or document link from
    // this browser does not carry over.
    if (guest) {
      const candidate = hash || saved;
      if (candidate && canSeeMenuItem(candidate, { userEmail: signedInEmail, permissionsLoaded: true })) {
        return candidate;
      }
      return 'piping-insulation';
    }
    if (hash) return hash;
    // Default landing: GA Dashboards (schedule overlays on GA drawings).
    // Hash / saved localStorage still win when present. Non-Construction
    // users are redirected to home once permissions load (see effect below).
    return saved || 'schedule-ga-dashboards';
  };
  const [activeMenuItem, setActiveMenuItem] = useState(getInitialMenuItem);
  usePortalUsageTracker(activeMenuItem);
  const isInternalNav = useRef(false);
  const defaultLandingChecked = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(`project_${projectId}_activeMenu`, activeMenuItem);
    } catch (e) { /* ignore */ }
  }, [activeMenuItem, projectId]);

  // A guest who lands on, or is sent to, a staff page goes to Progress Tracking.
  useEffect(() => {
    if (!guest) return;
    if (canSeeMenuItem(activeMenuItem, { userEmail: signedInEmail, permissionsLoaded: true })) return;
    setActiveMenuItem('piping-insulation');
  }, [guest, signedInEmail, activeMenuItem]);

  // Gate GA default landing: only Construction (schedule) users stay on it.
  useEffect(() => {
    if (defaultLandingChecked.current || !accounts?.[0] || activeMenuItem !== 'schedule-ga-dashboards') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info');
        if (cancelled || !res.data?.success) return;
        const perms = res.data.data || {};
        const depts = perms.allDepartmentIds || [];
        const canSchedule = !!(perms.isSuperadmin || perms.isAdmin || canAccessResource('schedule', depts));
        defaultLandingChecked.current = true;
        if (!canSchedule) {
          const hash = window.location.hash.replace('#', '');
          // Don't fight an explicit deep-link hash the user typed; only bounce the soft default.
          if (!hash || hash === 'schedule-ga-dashboards') {
            setActiveMenuItem('home');
          }
        }
      } catch {
        defaultLandingChecked.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, [instance, accounts, activeMenuItem]);

  const initialHashSet = useRef(false);
  useEffect(() => {
    const currentHash = window.location.hash.replace('#', '');
    if (currentHash !== activeMenuItem) {
      if (isInternalNav.current) {
        isInternalNav.current = false;
        return;
      }
      if (!initialHashSet.current) {
        initialHashSet.current = true;
        window.history.replaceState({ menu: activeMenuItem }, '', `#${activeMenuItem}`);
      } else {
        window.history.pushState({ menu: activeMenuItem }, '', `#${activeMenuItem}`);
      }
    } else if (!initialHashSet.current) {
      initialHashSet.current = true;
      window.history.replaceState({ menu: activeMenuItem }, '', `#${activeMenuItem}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenuItem]);

  // Listen for browser back/forward and sync activeMenuItem
  useEffect(() => {
    const handlePopState = (e) => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        isInternalNav.current = true;
        setActiveMenuItem(hash);
      } else if (e.state?.menu) {
        isInternalNav.current = true;
        setActiveMenuItem(e.state.menu);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  const [projectDetails, setProjectDetails] = useState(null);
  const [loadingProjectDetails, setLoadingProjectDetails] = useState(true);
  const [projectLoadError, setProjectLoadError] = useState('');
  const { toast, showToast, hideToast } = useToast();

  // ============================================
  // Permission State
  // ============================================
  const [permissions, setPermissions] = useState({
    matrix: false,
    actualSpent: false,
    export: false,
    edit: false,
    matBore: false,
    projectConfig: false,
    manageProjectUsers: false,
    loading: true,
  });
  const [userPermissionsData, setUserPermissionsData] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // ============================================
  // Modal State - Piping
  // ============================================
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedMatrixId, setSelectedMatrixId] = useState(null);
  const [selectedLineTag, setSelectedLineTag] = useState(null);
  // showColumnConfigModal removed - piping now uses columnConfigDiscipline state
  const [showMatBoreModal, setShowMatBoreModal] = useState(false);
  const [showManageTeamModal, setShowManageTeamModal] = useState(false);
  const [rulesOfCreditDiscipline, setRulesOfCreditDiscipline] = useState(null);
  const [columnConfigDiscipline, setColumnConfigDiscipline] = useState(null);
  const [disciplineVisibleColumns, setDisciplineVisibleColumns] = useState(() => {
    const disciplines = ['piping', 'piping-nde', 'piping-testing', 'mechanical', 'vessels', 'skids', 'chutes', 'conveyors', 'structural', 'hvac', 'ducts', 'boilermaker', 'civil', 'electrical'];
    const loaded = {};
    disciplines.forEach(d => {
      try {
        const saved = localStorage.getItem(`${d}_${projectId}_visibleColumns`);
        if (saved) loaded[d] = new Set(JSON.parse(saved));
      } catch {}
    });
    return loaded;
  });
  const [disciplineColumnOrder, setDisciplineColumnOrder] = useState(() => {
    const disciplines = ['piping', 'piping-nde', 'piping-testing', 'mechanical', 'vessels', 'skids', 'chutes', 'conveyors', 'structural', 'hvac', 'ducts', 'boilermaker', 'civil', 'electrical'];
    const loaded = {};
    disciplines.forEach(d => {
      try {
        const saved = localStorage.getItem(`${d}_${projectId}_columnOrder`);
        if (saved) loaded[d] = JSON.parse(saved);
      } catch {}
    });
    return loaded;
  });
  const [showContractorCriteriaModal, setShowContractorCriteriaModal] = useState(false);
  // Load selected department from localStorage and listen for changes
  const [selectedDepartment, setSelectedDepartment] = useState(() => {
    try {
      return localStorage.getItem('selectedDepartment') || 'projects';
    } catch {
      return 'projects';
    }
  });
  
  // Listen for department changes (from Header dropdown)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedDepartment' && e.newValue) {
        setSelectedDepartment(e.newValue);
      }
    };
    
    // Also poll for changes since storage event doesn't fire in same tab
    const pollInterval = setInterval(() => {
      const current = localStorage.getItem('selectedDepartment') || 'projects';
      setSelectedDepartment(prev => prev !== current ? current : prev);
    }, 100);
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, []);
  
  // Default to collapsed, but respect user preference from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      // If no preference saved, default to collapsed (true)
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  // Save sidebar collapsed state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [sidebarCollapsed]);
  const [showTutorial, setShowTutorial] = useState(false);
  const [user, setUser] = useState(null);

  // ============================================
  // Modal State - Other Features
  // ============================================
  // Unified export modal state - tracks which discipline is being exported
  const [exportDiscipline, setExportDiscipline] = useState(null); // 'piping', 'hvac', 'mechanical', 'ducts', 'boilermaker', 'vessels', 'structural', 'civil', 'electrical'
  
  const [showHvacUploadModal, setShowHvacUploadModal] = useState(false);
  const [showRotatingEquipmentUploadModal, setShowRotatingEquipmentUploadModal] = useState(false);
  const [showDuctsUploadModal, setShowDuctsUploadModal] = useState(false);
  const [showBoilermakerUploadModal, setShowBoilermakerUploadModal] = useState(false);
  const [showVesselsUploadModal, setShowVesselsUploadModal] = useState(false);
  const [showStructuralSteelUploadModal, setShowStructuralSteelUploadModal] = useState(false);
  const [showCivilUploadModal, setShowCivilUploadModal] = useState(false);
  const [showElectricalUploadModal, setShowElectricalUploadModal] = useState(false);
  const [showMechanicalUnifiedUploadModal, setShowMechanicalUnifiedUploadModal] = useState(false);
  
  // CCR (Change Orders) modal state
  const [showCCRUploadModal, setShowCCRUploadModal] = useState(false);
  
  // Matrix refresh key - increment to trigger data refresh in DisciplineMatrix
  const [matrixRefreshKey, setMatrixRefreshKey] = useState(0);
  const [showCCRExportModal, setShowCCRExportModal] = useState(false);
  const [ccrExporting, setCcrExporting] = useState(false);
  
  // Matrix filter state - passed up from DisciplineMatrix to display in header
  const [matrixFilterInfo, setMatrixFilterInfo] = useState(null);
  
  // Mechanical sub-table state - allows switching between rotating equipment, vessels, etc.
  // ?mechTable=<id> (links from the global tag explorer) preselects the sub-table.
  const [mechanicalSubTableId, setMechanicalSubTableId] = useState(() => {
    const hinted = new URLSearchParams(window.location.search).get('mechTable');
    return hinted && MECHANICAL_SUB_TABLES.some(t => t.id === hinted) ? hinted : 'rotating-equipment';
  });

  // Piping sub-view state - allows switching between Progress Tracker, NDE Tracker, Welder Log
  const [pipingSubViewId, setPipingSubViewId] = useState(() => {
    try {
      return localStorage.getItem(`pipingSubView_${projectId}`) || 'progress-tracker';
    } catch { return 'progress-tracker'; }
  });
  const [showNdeImportModal, setShowNdeImportModal] = useState(false);
  const [showNdeAuditLogModal, setShowNdeAuditLogModal] = useState(false);
  const [showPipeTestingImportModal, setShowPipeTestingImportModal] = useState(false);
  const [showPipeTestingAuditLogModal, setShowPipeTestingAuditLogModal] = useState(false);

  // ============================================
  // Job State
  // ============================================
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeExportJobId, setActiveExportJobId] = useState(null);
  const [exportingData, setExportingData] = useState(false);
  /* eslint-disable no-unused-vars */
  const [hvacExportJobId, setHvacExportJobId] = useState(null);
  const [hvacExporting, setHvacExporting] = useState(false);
  const [rotatingEquipmentExportJobId, setRotatingEquipmentExportJobId] = useState(null);
  const [rotatingEquipmentExporting, setRotatingEquipmentExporting] = useState(false);
  const [ductsExportJobId, setDuctsExportJobId] = useState(null);
  const [ductsExporting, setDuctsExporting] = useState(false);
  const [vesselsExportJobId, setVesselsExportJobId] = useState(null);
  const [vesselsExporting, setVesselsExporting] = useState(false);
  const [structuralSteelExportJobId, setStructuralSteelExportJobId] = useState(null);
  const [structuralSteelExporting, setStructuralSteelExporting] = useState(false);
  /* eslint-enable no-unused-vars */

  // ============================================
  // Column Mappings (for piping matrix)
  // ============================================
  const [columnMappings, setColumnMappings] = useState([]);

  // Ref to track downloaded jobs to prevent duplicate downloads
  const downloadedJobsRef = useRef(new Set());

  // ============================================
  // Data Fetching
  // ============================================

  // Fetch project details
  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        setLoadingProjectDetails(true);
        setProjectLoadError('');
        if (hasSession && projectId) {
          const projectResponse = await authenticatedApiCall(
            instance,
            accounts,
            'GET',
            `/projects/${projectId}`
          );

          if (projectResponse.data.success) {
            setProjectDetails(projectResponse.data.project);
          } else {
            setProjectDetails(null);
            setProjectLoadError(projectResponse.data.message || `No job matched "${projectId}". Use Select Project or the job number (for example 71940).`);
          }
        }
      } catch (error) {
        console.error('Error fetching project details:', error);
        setProjectDetails(null);
        const status = error.response?.status;
        const apiMsg = error.response?.data?.message || error.message;
        if (status === 403) {
          setProjectLoadError('You do not have permission to view this project.');
        } else if (status === 404) {
          setProjectLoadError(apiMsg || `No job matched "${projectId}". Use Select Project or the job number (for example 71940).`);
        } else {
          setProjectLoadError(apiMsg || 'Failed to load this project. The jobs were not deleted — the server could not read the project row.');
        }
      }
      setLoadingProjectDetails(false);
    };

    fetchProjectDetails();
  }, [instance, accounts, hasSession, projectId]);

  // Fetch permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        if (hasSession) {
          const response = await authenticatedApiCall(
            instance,
            accounts,
            'GET',
            '/auth/permissions-info'
          );

          if (response.data.success) {
            const data = response.data.data;
            const userPerms = data.permissions || [];
            const isAdmin = data.isAdmin || false;

            setUserPermissionsData({
              permissions: userPerms,
              isAdmin,
              isElevated: data.isElevated || false,
            })
            
            // Create user object for tutorial
            setUser({
              roles: data.roles || [{
                role_name: data.isAdmin ? 'admin' : (data.isElevated ? 'pm' : 'viewer'),
                role_id: null
              }],
              permissions: userPerms
            });;

            setPermissions({
              matrix: isAdmin || userPerms.includes('matrix.read') || userPerms.includes('matrix.update') || userPerms.includes('matrix.upload'),
              export: isAdmin || userPerms.includes('matrix.export'),
              edit: isAdmin || userPerms.includes('matrix.update'),
              matBore: isAdmin || userPerms.includes('mat_bore.edit'),
              projectConfig: isAdmin || userPerms.includes('project_config.access'),
              manageProjectUsers: isAdmin || userPerms.includes('projects.assign'),
              loading: false,
            });

            setPermissionsLoaded(true);
          }
        }
      } catch (error) {
        console.error('Error fetching permissions:', error);
        setPermissions(prev => ({ ...prev, loading: false }));
        setPermissionsLoaded(true);
      }
    };

    fetchPermissions();
  }, [instance, accounts, hasSession]);

  // Check if user can manage project users
  useEffect(() => {
    const checkCanManageProjectUsers = async () => {
      try {
        if (hasSession && projectId) {
          const response = await authenticatedApiCall(
            instance,
            accounts,
            'GET',
            `/project-assignments/can-manage/${projectId}`
          );

          if (response.data.success) {
            setPermissions(prev => ({
              ...prev,
              manageProjectUsers: response.data.canManage,
            }));
          }
        }
      } catch (error) {
        console.error('Error checking project user management permission:', error);
      }
    };

    checkCanManageProjectUsers();
  }, [instance, accounts, hasSession, projectId]);


  // Fetch column mappings
  const fetchColumnMappings = useCallback(async () => {
    try {
      if (hasSession && projectId) {
        const mappingsResponse = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/matrix/project/${projectId}/column-mappings`
        );

        if (mappingsResponse.data.success) {
          setColumnMappings(mappingsResponse.data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching column mappings:', error);
      setColumnMappings([]);
    }
  }, [instance, accounts, hasSession, projectId]);

  useEffect(() => {
    fetchColumnMappings();
  }, [fetchColumnMappings]);

  // ============================================
  // Menu Item Click Handler
  // ============================================
  const handleMenuItemClick = useCallback((menuItem) => {
    // Modal-triggering items - open modal without changing activeMenuItem
    switch (menuItem) {
      case 'upload-matrix':
        setShowUploadModal(true);
        break;
      case 'matbore-percent':
        setShowMatBoreModal(true);
        break;
      case 'export-excel':
        setExportDiscipline('piping');
        break;
      case 'column-config':
        setColumnConfigDiscipline('piping');
        break;
      case 'hvac-export-excel':
        setExportDiscipline('hvac');
        break;
      case 'hvac-upload-tracker':
        setShowHvacUploadModal(true);
        break;
      case 'mechanical-rotating-eqp-export-excel':
        setExportDiscipline('mechanical');
        break;
      case 'mechanical-rotating-eqp-upload-tracker':
        setShowRotatingEquipmentUploadModal(true);
        break;
      case 'mechanical-ducts-export-excel':
        setExportDiscipline('ducts');
        break;
      case 'mechanical-ducts-upload-tracker':
        setShowDuctsUploadModal(true);
        break;
      case 'boilermaker-export-excel':
        setExportDiscipline('boilermaker');
        break;
      case 'boilermaker-upload-tracker':
        setShowBoilermakerUploadModal(true);
        break;
      case 'mechanical-vessels-export-excel':
        setExportDiscipline('vessels');
        break;
      case 'mechanical-vessels-upload-tracker':
        setShowVesselsUploadModal(true);
        break;
      case 'structure-export-excel':
        setExportDiscipline('structural');
        break;
      case 'structure-upload-tracker':
        setShowStructuralSteelUploadModal(true);
        break;
      case 'civil-export-excel':
        setExportDiscipline('civil');
        break;
      case 'electrical-export-excel':
        setExportDiscipline('electrical');
        break;
      case 'manage-team':
        setShowManageTeamModal(true);
        break;
      case 'procurement-contractor-selection-criterias':
        setShowContractorCriteriaModal(true);
        break;
      case 'lgl-portal-tour':
        setShowTutorial(prev => !prev);
        break;
      default:
        // For all other menu items, change the active menu item normally
        setActiveMenuItem(menuItem);
        // Clear filter info when navigating away from pages that use it
        const filterableMenuItems = [
          'matrix', 'piping-insulation',
          'hvac-progress-tracker', 'hvac-progress-tracker-update',
          'mechanical-rotating-eqp', 'mechanical-rotating-eqp-progress-tracker-update',
          'mechanical-ducts', 'mechanical-ducts-progress-tracker-update',
          'boilermaker', 'boilermaker-progress-tracker-update',
          'mechanical-vessels', 'mechanical-vessels-progress-tracker-update',
          'structure', 'structure-progress-tracker-update',
          'civil', 'civil-progress-tracker-update',
          'electrical', 'electrical-progress-tracker-update',
          'actual-spent-hours',
          'mto', 'transmittal-log'
        ];
        if (!filterableMenuItems.includes(menuItem)) {
          setMatrixFilterInfo(null);
        }
        break;
    }
  }, []);

  // Toast close handler (alias for hideToast)
  const closeToast = hideToast;

  // ============================================
  // Memoized Modal Close Handlers (Performance Optimization)
  // ============================================
  // Piping modal handlers
  const handleCloseUploadModal = useCallback(() => setShowUploadModal(false), []);
  const handleCloseProgressModal = useCallback(() => {
    setShowProgressModal(false);
    setSelectedMatrixId(null);
    setSelectedLineTag(null);
  }, []);
  // handleCloseColumnConfigModal removed - piping now uses handleCloseDisciplineColumnConfig
  const handleCloseMatBoreModal = useCallback(() => setShowMatBoreModal(false), []);
  const handleCloseManageTeamModal = useCallback(() => setShowManageTeamModal(false), []);
  const handleCloseContractorCriteriaModal = useCallback(() => setShowContractorCriteriaModal(false), []);
  const handleCloseExportModal = useCallback(() => setExportDiscipline(null), []);
  const handleCloseDisciplineColumnConfig = useCallback(() => setColumnConfigDiscipline(null), []);
  const handleCloseRulesOfCredit = useCallback(() => setRulesOfCreditDiscipline(null), []);
  const handleCloseTutorial = useCallback(() => setShowTutorial(false), []);

  // Discipline upload modal handlers
  const handleCloseHvacUpload = useCallback(() => setShowHvacUploadModal(false), []);
  const handleCloseRotatingEquipmentUpload = useCallback(() => setShowRotatingEquipmentUploadModal(false), []);
  const handleCloseDuctsUpload = useCallback(() => setShowDuctsUploadModal(false), []);
  const handleCloseBoilermakerUpload = useCallback(() => setShowBoilermakerUploadModal(false), []);
  const handleCloseVesselsUpload = useCallback(() => setShowVesselsUploadModal(false), []);
  const handleCloseStructuralSteelUpload = useCallback(() => setShowStructuralSteelUploadModal(false), []);
  const handleCloseCivilUpload = useCallback(() => setShowCivilUploadModal(false), []);
  const handleCloseElectricalUpload = useCallback(() => setShowElectricalUploadModal(false), []);
  const handleCloseCCRUpload = useCallback(() => setShowCCRUploadModal(false), []);
  const handleCloseCCRExport = useCallback(() => setShowCCRExportModal(false), []);

  // Sidebar handlers
  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);
  const handleMobileClose = useCallback(() => setMobileSidebarOpen(false), []);

  // Generic toast handlers for modals
  const handleSaveSuccess = useCallback((message) => showToast(message, 'success'), [showToast]);
  const handleSaveError = useCallback((message) => showToast(message, 'error'), [showToast]);

  // Discipline upload success handlers (uploads are synchronous - refresh the matrix)
  const handleHvacUploadSuccess = useCallback((data) => {
    showToast(data.message || `HVAC Tracking upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success');
    setMatrixRefreshKey(prev => prev + 1);
  }, [showToast]);
  const handleHvacUploadError = useCallback((message) => {
    showToast(`HVAC Tracking upload failed: ${message}`, 'error');
  }, [showToast]);

  const handleRotatingEquipmentUploadSuccess = useCallback((data) => {
    showToast(data.message || `Rotating Equipment upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success');
    setMatrixRefreshKey(prev => prev + 1);
  }, [showToast]);
  const handleRotatingEquipmentUploadError = useCallback((message) => {
    showToast(`Rotating Equipment upload failed: ${message}`, 'error');
  }, [showToast]);

  const handleDuctsUploadSuccess = useCallback((data) => {
    showToast(data.message || `Ducts Tracking upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success');
    setMatrixRefreshKey(prev => prev + 1);
  }, [showToast]);
  const handleDuctsUploadError = useCallback((message) => {
    showToast(`Ducts Tracking upload failed: ${message}`, 'error');
  }, [showToast]);

  const handleBoilermakerUploadSuccess = useCallback((data) => {
    showToast(data.message || `Boilermaker Tracking upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success');
    setMatrixRefreshKey(prev => prev + 1);
  }, [showToast]);
  const handleBoilermakerUploadError = useCallback((message) => {
    showToast(`Boilermaker Tracking upload failed: ${message}`, 'error');
  }, [showToast]);

  const handleVesselsUploadSuccess = useCallback((data) => {
    showToast(data.message || `Vessels Tracking upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success');
    setMatrixRefreshKey(prev => prev + 1);
  }, [showToast]);
  const handleVesselsUploadError = useCallback((message) => {
    showToast(`Vessels Tracking upload failed: ${message}`, 'error');
  }, [showToast]);

  const handleStructuralSteelUploadSuccess = useCallback((data) => {
    showToast(data.message || `Structural Steel Tracking upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success');
    setMatrixRefreshKey(prev => prev + 1);
  }, [showToast]);
  const handleStructuralSteelUploadError = useCallback((message) => {
    showToast(`Structural Steel Tracking upload failed: ${message}`, 'error');
  }, [showToast]);

  const handleCivilUploadSuccess = useCallback((data) => {
    showToast(data.message || `Civil Tracking upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success');
    setMatrixRefreshKey(prev => prev + 1);
  }, [showToast]);
  const handleCivilUploadError = useCallback((message) => {
    showToast(`Civil Tracking upload failed: ${message}`, 'error');
  }, [showToast]);

  const handleElectricalUploadSuccess = useCallback((data) => {
    showToast(data.message || `Electrical Tracking upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success');
    setMatrixRefreshKey(prev => prev + 1);
  }, [showToast]);
  const handleElectricalUploadError = useCallback((message) => {
    showToast(`Electrical Tracking upload failed: ${message}`, 'error');
  }, [showToast]);

  const handleCCRUploadSuccess = useCallback((data) => {
    showToast(`Change Orders upload queued! Job ID: ${data.jobId}`, 'success');
  }, [showToast]);
  const handleCCRUploadError = useCallback((message) => {
    showToast(`Change Orders upload failed: ${message}`, 'error');
  }, [showToast]);

  // Column config save handler - now inlined in DisciplineColumnConfigModal onSave prop

  // Rules of credit save handler
  const handleRulesOfCreditSave = useCallback(() => {
    showToast('Rules of Credit saved successfully!', 'success');
    // Force the matrix to refetch so recalculated totals appear without a manual reload
    setMatrixRefreshKey((k) => k + 1);
  }, [showToast]);

  // ============================================
  // Upload Handlers
  // ============================================
  const handleUploadSuccess = (data) => {
    if (typeof data === 'string') {
      showToast(data, 'success');
      const jobIdMatch = data.match(/Job ID: (\d+)/);
      if (jobIdMatch) {
        setActiveJobId(jobIdMatch[1]);
      }
    } else {
      const { message, jobId, totalJobs } = data;
      const displayMessage = totalJobs > 1
        ? `${message} (${totalJobs} jobs queued, monitoring last job)`
        : `${message} (Job ID: ${jobId})`;
      showToast(displayMessage, 'success');
      if (jobId) {
        setActiveJobId(String(jobId));
      }
    }
  };

  const handleUploadError = (message) => {
    showToast(message, 'error');
  };

  // ============================================
  // Job Complete Handlers
  // ============================================
  const handleJobComplete = useCallback(async (jobData) => {
    setActiveJobId(null);
    const parsedJobData = jobData.job_data ? (typeof jobData.job_data === 'string' ? JSON.parse(jobData.job_data) : jobData.job_data) : {};
    if (parsedJobData.skippedDeletions > 0) {
      showToast(
        `Import completed with ${jobData.total_rows} rows processed, but ${parsedJobData.skippedDeletions} deletion(s) were skipped — you do not have permission to delete rows. Contact an administrator if you need delete access.`,
        'warning'
      );
    } else {
      showToast(`Job completed successfully! ${jobData.total_rows} rows processed.`, 'success');
    }
    // Refresh column mappings (in case new UD columns were added)
    await fetchColumnMappings();
    // Trigger matrix data refresh
    setMatrixRefreshKey(prev => prev + 1);
  }, [fetchColumnMappings, showToast]);

  const handleJobError = (errorMessage) => {
    showToast(`Job failed: ${errorMessage}`, 'error');
    setActiveJobId(null);
  };

  // ============================================
  // Download Handler
  // ============================================
  const triggerDownload = async (jobId) => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0]
      });

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/matrix/download/${jobId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenResponse.idToken}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.status} - ${errorText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers.get('content-disposition');
      let filename = `project_${projectId}_export.xlsx`;

      if (contentDisposition) {
        const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
        if (rfc5987Match && rfc5987Match[1]) {
          filename = decodeURIComponent(rfc5987Match[1]);
        } else {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast(`Error downloading file: ${error.message}`, 'error');
    }
  };

  // ============================================
  // Template Download Handler
  // ============================================
  const handleDownloadTemplate = async (discipline = 'piping') => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0]
      });

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      
      // Map discipline to template endpoint
      // All mechanical sub-tables (rotating, vessels, chutes, conveyors) use the same unified template
      const mechanicalDisciplines = ['mechanical', 'vessels', 'chutes', 'conveyors'];
      const templateEndpoints = {
        piping: `/matrix/template/${projectId}`,
        mechanical: `/mechanical/template/${projectId}`,
        hvac: `/discipline-matrix/hvac/template/${projectId}`,
        ducts: `/discipline-matrix/ducts/template/${projectId}`,
        boilermaker: `/discipline-matrix/boilermaker/template/${projectId}`,
        structural: `/discipline-matrix/structural/template/${projectId}`,
        civil: `/discipline-matrix/civil/template/${projectId}`,
        electrical: `/discipline-matrix/electrical/template/${projectId}`,
        'piping-testing': `/pipe-testing/template`,
      };
      
      // Route all mechanical sub-tables to the unified mechanical template
      const effectiveDiscipline = mechanicalDisciplines.includes(discipline) ? 'mechanical' : discipline;
      const endpoint = templateEndpoints[effectiveDiscipline] || `/matrix/template/${projectId}`;
      
      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenResponse.idToken}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Template download failed: ${response.status} - ${errorText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers.get('content-disposition');
      let filename = `${discipline}_template_${projectId}.xlsx`;

      if (contentDisposition) {
        const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
        if (rfc5987Match && rfc5987Match[1]) {
          filename = decodeURIComponent(rfc5987Match[1]);
        } else {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast(`${discipline.charAt(0).toUpperCase() + discipline.slice(1)} template downloaded successfully`, 'success');
    } catch (error) {
      console.error('Error downloading template:', error);
      showToast(`Error downloading template: ${error.message}`, 'error');
    }
  };

  // ============================================
  // CCR Handlers
  // ============================================
  const handleCCRDownloadTemplate = async () => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0]
      });

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/ccr-tracking/template/${projectDetails?.job_number}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenResponse.idToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Template download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ccr_template_${projectDetails?.job_number}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast('CCR template downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading CCR template:', error);
      showToast(`Error downloading template: ${error.message}`, 'error');
    }
  };

  const handleCCRExport = async (exportType) => {
    if (ccrExporting) return;
    
    try {
      setCcrExporting(true);
      setShowCCRExportModal(false);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/ccr-tracking/export/${projectDetails?.job_number}`,
        { exportType }
      );

      if (response.data.success) {
        const { jobId, filename, rowCount } = response.data.data;
        showToast(`CCR export completed! ${rowCount} rows exported. Downloading...`, 'success');
        
        // Download the file
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0]
        });
        
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        const downloadResponse = await fetch(`${apiUrl}/ccr-tracking/download/${jobId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${tokenResponse.idToken}`
          }
        });
        
        if (downloadResponse.ok) {
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } else {
        throw new Error(response.data.message || 'Export failed');
      }
    } catch (error) {
      console.error('Error exporting CCR data:', error);
      showToast(`Error exporting: ${error.message}`, 'error');
    } finally {
      setCcrExporting(false);
    }
  };

  // ============================================
  // Export Handlers
  // ============================================
  // Unified export function for all disciplines
  const exportDisciplineData = async (exportType = 'frontend', allMechanicalTabs = false, infoColumns, rowOpts = {}) => {
    if (exportingData || !exportDiscipline) return;

    try {
      setExportingData(true);
      const discipline = exportDiscipline;
      const rowIds = rowOpts.rows === 'filtered' && Array.isArray(rowOpts.rowIds) && rowOpts.rowIds.length
        ? rowOpts.rowIds
        : undefined;
      setExportDiscipline(null); // Close the modal
      
      // Piping uses the job queue system, NDE has its own export, other disciplines use direct download
      if (discipline === 'piping-nde') {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0]
        });

        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        const response = await fetch(`${apiUrl}/nde/export/${projectId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokenResponse.idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(rowIds ? { rowIds } : {}),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`NDE export failed: ${response.status} - ${errorText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const blob = new Blob([arrayBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers.get('content-disposition');
        let filename = `NDE_Tracker_Export.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportingData(false);
        showToast('NDE Tracker exported successfully!', 'success');
      } else if (discipline === 'piping-testing') {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0]
        });

        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        const response = await fetch(`${apiUrl}/pipe-testing/export/${projectId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokenResponse.idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(rowIds ? { rowIds } : {}),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Testing Tracker export failed: ${response.status} - ${errorText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const blob = new Blob([arrayBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers.get('content-disposition');
        let filename = `Pipe_Testing_Tracker_Export.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportingData(false);
        showToast('Testing Tracker exported successfully!', 'success');
      } else if (discipline === 'piping') {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          `/matrix/export/${projectId}`,
          { exportType, ...(rowIds ? { rowIds } : {}) }
        );

        if (response.data.success) {
          setActiveExportJobId(response.data.jobId);
          showToast(`Piping export started! Job ID: ${response.data.jobId}`, 'success');
        } else {
          throw new Error(response.data.message || 'Failed to start export job');
        }
      } else {
        // Other disciplines: direct download
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0]
        });

        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        const exportUrl = allMechanicalTabs
          ? `${apiUrl}/discipline-matrix/mechanical-export-all/${projectId}`
          : `${apiUrl}/discipline-matrix/${discipline}/export`;
        const response = await fetch(exportUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokenResponse.idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            job_number: projectDetails?.job_number,
            projectId,
            exportType,
            ...(allMechanicalTabs && infoColumns ? { infoColumns } : {}),
            ...(rowIds ? { rowIds } : {}),
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Export failed: ${response.status} - ${errorText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const blob = new Blob([arrayBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers.get('content-disposition');
        let filename = allMechanicalTabs ? `mechanical_all_export.xlsx` : `${discipline}_export.xlsx`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setExportingData(false);
        showToast(
          allMechanicalTabs
            ? 'All mechanical tabs exported successfully!'
            : `${discipline.charAt(0).toUpperCase() + discipline.slice(1)} exported successfully!`,
          'success'
        );
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      setExportingData(false);
      showToast(`Error exporting data: ${error.message}`, 'error');
    }
  };
  
  // Legacy function for backward compatibility
  // eslint-disable-next-line no-unused-vars
  const exportMatrixData = async (exportType = 'frontend') => {
    if (exportingData) return;
    // If no exportDiscipline is set, default to piping
    if (!exportDiscipline) {
      setExportDiscipline('piping');
    }
    
    try {
      setExportingData(true);
      setExportDiscipline(null);

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/matrix/export/${projectId}`,
        { exportType }
      );

      if (response.data.success) {
        setActiveExportJobId(response.data.jobId);
        showToast(`Export job started! Job ID: ${response.data.jobId}`, 'success');
      } else {
        throw new Error(response.data.message || 'Failed to start export job');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      setExportingData(false);
      showToast(`Error exporting data: ${error.message}`, 'error');
    }
  };

  const handleExportJobComplete = (jobData) => {
    showToast(`Export completed successfully! ${jobData.processed_rows} rows exported.`, 'success');
    setActiveExportJobId(null);
    setExportingData(false);

    if (jobData.job_id && !downloadedJobsRef.current.has(jobData.job_id)) {
      downloadedJobsRef.current.add(jobData.job_id);
      triggerDownload(jobData.job_id);
    }
  };

  const handleExportJobError = (errorMessage) => {
    showToast(`Export failed: ${errorMessage}`, 'error');
    setActiveExportJobId(null);
    setExportingData(false);
  };

  // Legacy export handlers removed - unified export now uses exportDisciplineData()

  // ============================================
  // Progress Modal Handlers
  // ============================================
  const handleLineTagClick = (matrixId, lineTag) => {
    setSelectedMatrixId(matrixId);
    setSelectedLineTag(lineTag);
    setShowProgressModal(true);
  };

  // eslint-disable-next-line no-unused-vars
  const closeProgressModal = () => {
    setShowProgressModal(false);
    setSelectedMatrixId(null);
    setSelectedLineTag(null);
  };

  // ============================================
  // Placeholder Component
  // ============================================
  const PlaceholderContent = ({ title, description, icon = '📋' }) => (
    <div style={{
      textAlign: 'center',
      padding: '3rem',
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{icon}</div>
      <h3 style={{ color: '#495057', marginBottom: '0.5rem' }}>{title}</h3>
      <p style={{ color: '#6c757d', fontSize: '1rem' }}>{description}</p>
    </div>
  );

  // ============================================
  // Content Renderer
  // ============================================
  const renderContent = () => {
    switch (activeMenuItem) {
      // Home Page
      case 'home':
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            padding: '2rem'
          }}>
            <h1 style={{ 
              fontSize: '3.5rem', 
              color: '#333', 
              marginBottom: '1.5rem',
              fontWeight: '700',
              letterSpacing: '-1px'
            }}>
              LGL PORTAL
            </h1>
            <div style={{ 
              width: '80px', 
              height: '4px', 
              backgroundColor: '#dc3545', 
              marginBottom: '2rem'
            }}></div>
            <h2 style={{ 
              fontSize: '1.75rem', 
              color: '#555', 
              fontWeight: '300',
              maxWidth: '800px',
              lineHeight: '1.4'
            }}>
              One single platform to manage the whole organization
            </h2>
          </div>
        );

      // Main matrix - uses optimized DisciplineMatrix
      // Key forces complete unmount/remount when switching away, ensuring Socket.IO cleanup
      case 'matrix': {
        const handlePipingSubViewChange = (newSubViewId) => {
          setPipingSubViewId(newSubViewId);
          try { localStorage.setItem(`pipingSubView_${projectId}`, newSubViewId); } catch {}
        };

        if (pipingSubViewId === 'nde-tracker') {
          return (
            <DisciplineMatrix
              key={`matrix-${projectId}-piping-nde`}
              disciplineId="piping-nde"
              projectId={projectId}
              projectDetails={projectDetails}
              permissions={permissions}
              onUploadClick={() => setShowNdeImportModal(true)}
              onExportClick={() => setExportDiscipline('piping-nde')}
              onDownloadTemplateClick={() => handleDownloadTemplate('piping-nde')}
              onColumnConfigClick={() => setColumnConfigDiscipline('piping-nde')}
              onAuditLogClick={() => setShowNdeAuditLogModal(true)}
              onNdeImportClick={() => setShowNdeImportModal(true)}
              visibleColumns={disciplineVisibleColumns['piping-nde']}
              columnOrder={disciplineColumnOrder['piping-nde']}
              refreshKey={matrixRefreshKey}
              onFilterStateChange={setMatrixFilterInfo}
              pipingSubViewId={pipingSubViewId}
              onPipingSubViewChange={handlePipingSubViewChange}
            />
          );
        }

        if (pipingSubViewId === 'testing-tracker') {
          return (
            <DisciplineMatrix
              key={`matrix-${projectId}-piping-testing`}
              disciplineId="piping-testing"
              projectId={projectId}
              projectDetails={projectDetails}
              permissions={permissions}
              onUploadClick={() => setShowPipeTestingImportModal(true)}
              onExportClick={() => setExportDiscipline('piping-testing')}
              onDownloadTemplateClick={() => handleDownloadTemplate('piping-testing')}
              onColumnConfigClick={() => setColumnConfigDiscipline('piping-testing')}
              onAuditLogClick={() => setShowPipeTestingAuditLogModal(true)}
              visibleColumns={disciplineVisibleColumns['piping-testing']}
              columnOrder={disciplineColumnOrder['piping-testing']}
              refreshKey={matrixRefreshKey}
              onFilterStateChange={setMatrixFilterInfo}
              pipingSubViewId={pipingSubViewId}
              onPipingSubViewChange={handlePipingSubViewChange}
            />
          );
        }
        
        if (pipingSubViewId === 'welder-log') {
          return (
            <WelderLog
              projectId={projectId}
              projectDetails={projectDetails}
              permissions={permissions}
              pipingSubViewId={pipingSubViewId}
              onPipingSubViewChange={handlePipingSubViewChange}
            />
          );
        }

        return (
          <DisciplineMatrix
            key={`matrix-${projectId}-piping`}
            disciplineId="piping"
            projectId={projectId}
            projectDetails={projectDetails}
            permissions={permissions}
            onLineTagClick={handleLineTagClick}
            onUploadClick={() => setShowUploadModal(true)}
            onExportClick={() => setExportDiscipline('piping')}
            onDownloadTemplateClick={() => handleDownloadTemplate('piping')}
            onColumnConfigClick={() => setColumnConfigDiscipline('piping')}
            onMatBoreClick={() => setShowMatBoreModal(true)}
            onManageTeamClick={() => setShowManageTeamModal(true)}
            onManageUsersClick={() => setShowManageTeamModal(true)}
            onRulesOfCreditClick={() => setRulesOfCreditDiscipline('piping')}
            columnMappings={columnMappings}
            onColumnMappingsChange={setColumnMappings}
            visibleColumns={disciplineVisibleColumns['piping']}
            columnOrder={disciplineColumnOrder['piping']}
            refreshKey={matrixRefreshKey}
            onFilterStateChange={setMatrixFilterInfo}
            pipingSubViewId={pipingSubViewId}
            onPipingSubViewChange={handlePipingSubViewChange}
          />
        );
      }

      // Actuals
      case 'actual-spent-hours':
        return <ActualSpentLog projectId={projectId} onFilterStateChange={setMatrixFilterInfo} />;

      // Material Management
      case 'material-log':
      case 'material-log-view':
        return <MaterialLog projectId={projectId} jobNumber={projectDetails?.job_number} />;
      case 'material-requisition-log':
        return <RequisitionLogPage projectId={projectId} projectDetails={projectDetails} />;
      case 'tariff':
      case 'tariff-view':
        return <TariffLog />;
      case 'purchasing-list':
      case 'purchasing-list-view':
        return <PurchasingListLog />;
      
      case 'currency-exchange':
        return <CurrencyExchange />;

      // Progress Tracking - All Disciplines using DisciplineMatrix
      case 'hvac':
      case 'hvac-progress-tracker-update':
        return (
          <DisciplineMatrix 
            disciplineId="hvac"
            projectId={projectId} 
            projectDetails={projectDetails}
            onUploadClick={() => setShowHvacUploadModal(true)}
            onExportClick={() => setExportDiscipline('hvac')}
            onDownloadTemplateClick={() => handleDownloadTemplate('hvac')}
            onColumnConfigClick={() => setColumnConfigDiscipline('hvac')}
            onRulesOfCreditClick={() => setRulesOfCreditDiscipline('hvac')}
            visibleColumns={disciplineVisibleColumns['hvac']}
            columnOrder={disciplineColumnOrder['hvac']}
            refreshKey={matrixRefreshKey}
            onFilterStateChange={setMatrixFilterInfo}
          />
        );
      case 'mechanical-rotating-eqp':
      case 'mechanical-rotating-eqp-progress-tracker-update':
      case 'mechanical-vessels':
      case 'mechanical-vessels-progress-tracker-update': {
        // Unified mechanical sub-table handling
        // Get the current sub-table config based on state
        const subTable = getSubTableById(mechanicalSubTableId);
        const currentDisciplineId = subTable.disciplineId;
        
        // Handler for sub-table change
        const handleMechanicalSubTableChange = (newSubTableId) => {
          setMechanicalSubTableId(newSubTableId);
          // Navigate to the appropriate menu item when switching tables
          const newSubTable = getSubTableById(newSubTableId);
          if (newSubTable.id === 'rotating-equipment') {
            setActiveMenuItem('mechanical-rotating-eqp-progress-tracker-update');
          } else if (newSubTable.id === 'vessels') {
            setActiveMenuItem('mechanical-vessels-progress-tracker-update');
          }
        };
        
        // Use unified mechanical upload modal for all mechanical sub-tables
        // The unified modal handles routing rows to appropriate tables based on Type column
        const handleUploadClick = () => {
          setShowMechanicalUnifiedUploadModal(true);
        };
        
        return (
          <DisciplineMatrix 
            disciplineId={currentDisciplineId}
            projectId={projectId} 
            projectDetails={projectDetails}
            onUploadClick={handleUploadClick}
            onExportClick={() => setExportDiscipline(currentDisciplineId)}
            onDownloadTemplateClick={() => handleDownloadTemplate(currentDisciplineId)}
            onColumnConfigClick={() => setColumnConfigDiscipline(currentDisciplineId)}
            onRulesOfCreditClick={() => setRulesOfCreditDiscipline(currentDisciplineId)}
            visibleColumns={disciplineVisibleColumns[currentDisciplineId]}
            columnOrder={disciplineColumnOrder[currentDisciplineId]}
            refreshKey={matrixRefreshKey}
            onFilterStateChange={setMatrixFilterInfo}
            mechanicalSubTableId={mechanicalSubTableId}
            onMechanicalSubTableChange={handleMechanicalSubTableChange}
          />
        );
      }
      case 'mechanical-ducts':
      case 'mechanical-ducts-progress-tracker-update':
        return (
          <DisciplineMatrix 
            disciplineId="ducts"
            projectId={projectId} 
            projectDetails={projectDetails}
            onUploadClick={() => setShowDuctsUploadModal(true)}
            onExportClick={() => setExportDiscipline('ducts')}
            onDownloadTemplateClick={() => handleDownloadTemplate('ducts')}
            onColumnConfigClick={() => setColumnConfigDiscipline('ducts')}
            onRulesOfCreditClick={() => setRulesOfCreditDiscipline('ducts')}
            visibleColumns={disciplineVisibleColumns['ducts']}
            columnOrder={disciplineColumnOrder['ducts']}
            refreshKey={matrixRefreshKey}
            onFilterStateChange={setMatrixFilterInfo}
          />
        );
      case 'boilermaker':
      case 'boilermaker-progress-tracker-update':
        return (
          <DisciplineMatrix
            disciplineId="boilermaker"
            projectId={projectId}
            projectDetails={projectDetails}
            onUploadClick={() => setShowBoilermakerUploadModal(true)}
            onExportClick={() => setExportDiscipline('boilermaker')}
            onDownloadTemplateClick={() => handleDownloadTemplate('boilermaker')}
            onColumnConfigClick={() => setColumnConfigDiscipline('boilermaker')}
            onRulesOfCreditClick={() => setRulesOfCreditDiscipline('boilermaker')}
            visibleColumns={disciplineVisibleColumns['boilermaker']}
            columnOrder={disciplineColumnOrder['boilermaker']}
            refreshKey={matrixRefreshKey}
            onFilterStateChange={setMatrixFilterInfo}
          />
        );
      case 'structure':
      case 'structure-progress-tracker-update':
        return (
          <DisciplineMatrix 
            disciplineId="structural"
            projectId={projectId} 
            projectDetails={projectDetails}
            onUploadClick={() => setShowStructuralSteelUploadModal(true)}
            onExportClick={() => setExportDiscipline('structural')}
            onDownloadTemplateClick={() => handleDownloadTemplate('structural')}
            onColumnConfigClick={() => setColumnConfigDiscipline('structural')}
            onRulesOfCreditClick={() => setRulesOfCreditDiscipline('structural')}
            visibleColumns={disciplineVisibleColumns['structural']}
            columnOrder={disciplineColumnOrder['structural']}
            refreshKey={matrixRefreshKey}
            onFilterStateChange={setMatrixFilterInfo}
          />
        );

      // MTO & Material Index
      case 'mto':
        return <MtoLog projectId={projectId} jobNumber={projectDetails?.job_number} onFilterStateChange={setMatrixFilterInfo} />;
      case 'material-index':
      case 'ottaviano':
        return <MaterialIndexLog projectId={projectId} />;
      
      // BOM Extraction
      case 'estimation-piping-bom-extraction':
        return <BomExtractionPage jobNumber={projectDetails?.job_number} />;
      
      // BOM Extractor API (ForwardPath external tool)
      case 'estimation-piping-bom-extractor-api':
        return <BomExtractorApiPage />;
      
      // BOM Extractor API 2 (Integrated PDF extraction)
      case 'estimation-piping-bom-extractor-api-2':
        return <BomExtractorApi2Page jobNumber={projectDetails?.job_number} />;
      
      // Forecasting
      case 'forecasting-dashboard1':
        return <ForecastingDashboard1 jobNumber={projectDetails?.job_number} />;
      case 'forecasting-budget':
        return <ForecastingPlaceholder title="Budget" />;
      case 'forecasting-actuals-accruals':
        return <ForecastingPlaceholder title="Actuals + Accruals" />;
      case 'forecasting-po':
        return <ForecastingPlaceholder title="PO" />;
      case 'forecasting-invoices':
        return <ForecastingPlaceholder title="Invoices" />;
      case 'forecasting-commitments':
        return <ForecastingPlaceholder title="Commitments" />;
      case 'forecasting-fwo':
        return <ForecastingPlaceholder title="FWO" />;
      case 'forecasting-co':
        return <ForecastingPlaceholder title="CO" />;
      case 'forecasting-ccr':
        return <ForecastingPlaceholder title="CCR" />;
      case 'forecasting-cash-flow':
        return <ForecastingPlaceholder title="Cash Flow" />;
      case 'forecasting-forecast':
        return <ForecastingPlaceholder title="Forecast" />;
      case 'forecasting-sop':
        return <ForecastingPlaceholder title="SOP" />;
      case 'forecasting-maestro-reports':
        return <MaestroReports projectId={projectId} projectDetails={projectDetails} />;
      case 'forecasting-billing':
        return <BillingDashboard projectId={projectId} projectDetails={projectDetails} />;
      
      case 'transmittal-log':
        return <TransmittalLog projectId={projectId} onFilterStateChange={setMatrixFilterInfo} />;
      case 'project-sharepoint':
      case 'documentationManagement-sharepoint': // legacy menu id
        return (
          <SharePointBookmarksPage
            projectId={projectId}
            rootUrl={projectDetails?.sharepoint_url || null}
            rootLabel="Open job SharePoint site"
          />
        );
      case 'project-autodesk-build':
        return (
          <SharePointBookmarksPage
            projectId={projectId}
            provider="autodesk_build"
            rootUrl={projectDetails?.autodesk_build_url || null}
            rootLabel="Open Autodesk Build project"
          />
        );
      case 'project-procore':
        return (
          <SharePointBookmarksPage
            projectId={projectId}
            provider="procore"
            rootUrl={projectDetails?.procore_url || null}
            rootLabel="Open Procore project"
          />
        );

      // Development & Reports
      case 'development-log':
        return <DevelopmentLog />;
      case 'code-refactoring':
        return <CodeRefactoringDashboard />;
      case 'file-upload':
        return <FileUploadPage />;
      case 'test-piping-progress-tracker':
        return <TestPipingProgressTracker projectId={projectId} jobNumber={projectDetails?.job_number} />;
      case 'power-bi-report':
        return <PowerBIReport projectId={projectId} />;

      // Processes
      case 'processes-requisitions':
        return <ProcessPdfViewer pdfType="requisitions" />;
      case 'processes-purchase-orders':
        return <ProcessPdfViewer pdfType="purchase-orders" />;
      case 'processes-invoices':
        return <ProcessPdfViewer pdfType="invoices" />;

      // Procurement - SelectedContractors is now a standalone route at /contractors
      // Navigation is handled by ProjectSidebar.handleMenuItemClick which redirects to /contractors
      // This case should not be reached, but if it is, show a redirect message
      case 'procurement-selected-contractors':
        return (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Redirecting to Contractors Database...</p>
          </div>
        );

      // Download App
      case 'download-app':
        return <DownloadApp />;

      // PMO Radiator
      case 'pmo-radiator':
        return <PMORadiator />;
      case 'pmo-usage':
        return <PortalUsageDashboard />;

      case 'subcontractor-discovery':
        return <SubcontractorDiscovery />;

      // Daily Report
      case 'daily-report':
        return <DailyReport />;

      // Technical Sheets (Autodesk Build-style)
      case 'technical-sheets':
        return <SheetsDashboard projectId={projectId} />;

      // Technical Specifications
      case 'technical-specifications':
        return <SpecificationsDashboard projectId={projectId} />;

      // Tutorial
      case 'lgl-portal-tour':
        return (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <h3 style={{ color: '#333', marginBottom: '1rem' }}>LGL Portal Tour</h3>
            <p style={{ color: '#666' }}>
              {showTutorial ? 'The interactive tour is currently active. Follow the highlighted steps to learn about the portal.' : 'Click the button below to start an interactive tour of the LGL Portal.'}
            </p>
            <button
              onClick={() => setShowTutorial(prev => !prev)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: showTutorial ? '#6b7280' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                marginTop: '1rem'
              }}
            >
              {showTutorial ? 'Stop Tour' : 'Start Tour'}
            </button>
          </div>
        );

      // Technical RFI Log
      case 'technical-rfis':
        return <RFILogPage projectId={projectId} />;

      case 'technical-requisitions':
        return <RequisitionLogPage projectId={projectId} projectDetails={projectDetails} />;

      // Technical Photos
      case 'technical-photos':
        return <PhotosLog projectId={projectId} />;

      // Technical Meetings
      case 'technical-meetings':
        return <MeetingsDashboard projectId={projectId} />;

      // Technical Issues
      case 'technical-issues':
        return <IssuesDashboard projectId={projectId} />;

      // Technical Submittals
      case 'technical-submittals':
        return <SubmittalsDashboard projectId={projectId} />;

      // Technical Checklists
      case 'technical-checklists':
        return <ChecklistsDashboard projectId={projectId} />;

      // Technical Assets
      case 'technical-assets':
        return <AssetsDashboard projectId={projectId} />;

      // Technical Forms (Autodesk Build-style with templates + form builder)
      case 'technical-forms':
        return <FormsDashboard projectId={projectId} />;

      // Technical Closeout (placeholder)
      case 'technical-closeout':
        return <PlaceholderContent title="Closeout / Handover" description="Manage project closeout, as-built documentation, and handover packages." icon="📦" />;

      // Placeholders for features not yet implemented
      case 'daily-reports':
        return <PlaceholderContent title="Daily Reports" description="View and manage daily project reports." icon="📊" />;
      case 'rfis':
        return <PlaceholderContent title="RFIs" description="Request for Information management and tracking." icon="❓" />;
      case 'checklists':
        return <PlaceholderContent title="Checklists" description="Manage project checklists and inspections." icon="✅" />;
      case 'permissions':
        return <PlaceholderContent title="Permissions" description="Manage documentation permissions and access controls." icon="🔐" />;
      case 'cost-breakdown':
        return <PlaceholderContent title="Cost Breakdown" description="View and analyze detailed cost breakdowns for project estimation." icon="💰" />;
      case 'cost-breakdown-rentals':
        return <RentalsLog />;
      case 'cost-breakdown-labour-rates':
        return <LabourRatesLog />;
      case 'piping-insulation': {
        const handlePipingSubViewChange = (newSubViewId) => {
          setPipingSubViewId(newSubViewId);
          try { localStorage.setItem(`pipingSubView_${projectId}`, newSubViewId); } catch {}
        };

        if (pipingSubViewId === 'nde-tracker') {
          return (
            <DisciplineMatrix
              key={`piping-nde-${projectId}`}
              disciplineId="piping-nde"
              projectId={projectId}
              projectDetails={projectDetails}
              permissions={permissions}
              onUploadClick={() => setShowNdeImportModal(true)}
              onExportClick={() => setExportDiscipline('piping-nde')}
              onDownloadTemplateClick={() => handleDownloadTemplate('piping-nde')}
              onColumnConfigClick={() => setColumnConfigDiscipline('piping-nde')}
              onAuditLogClick={() => setShowNdeAuditLogModal(true)}
              onNdeImportClick={() => setShowNdeImportModal(true)}
              visibleColumns={disciplineVisibleColumns['piping-nde']}
              columnOrder={disciplineColumnOrder['piping-nde']}
              refreshKey={matrixRefreshKey}
              onFilterStateChange={setMatrixFilterInfo}
              pipingSubViewId={pipingSubViewId}
              onPipingSubViewChange={handlePipingSubViewChange}
            />
          );
        }

        if (pipingSubViewId === 'testing-tracker') {
          return (
            <DisciplineMatrix
              key={`piping-testing-${projectId}`}
              disciplineId="piping-testing"
              projectId={projectId}
              projectDetails={projectDetails}
              permissions={permissions}
              onUploadClick={() => setShowPipeTestingImportModal(true)}
              onExportClick={() => setExportDiscipline('piping-testing')}
              onDownloadTemplateClick={() => handleDownloadTemplate('piping-testing')}
              onColumnConfigClick={() => setColumnConfigDiscipline('piping-testing')}
              onAuditLogClick={() => setShowPipeTestingAuditLogModal(true)}
              visibleColumns={disciplineVisibleColumns['piping-testing']}
              columnOrder={disciplineColumnOrder['piping-testing']}
              refreshKey={matrixRefreshKey}
              onFilterStateChange={setMatrixFilterInfo}
              pipingSubViewId={pipingSubViewId}
              onPipingSubViewChange={handlePipingSubViewChange}
            />
          );
        }

        if (pipingSubViewId === 'welder-log') {
          return (
            <WelderLog
              projectId={projectId}
              projectDetails={projectDetails}
              permissions={permissions}
              pipingSubViewId={pipingSubViewId}
              onPipingSubViewChange={handlePipingSubViewChange}
            />
          );
        }

        return (
          <DisciplineMatrix 
            disciplineId="piping"
            projectId={projectId} 
            projectDetails={projectDetails}
            onLineTagClick={handleLineTagClick}
            onUploadClick={() => setShowUploadModal(true)}
            onExportClick={() => setExportDiscipline('piping')}
            onDownloadTemplateClick={() => handleDownloadTemplate('piping')}
            onColumnConfigClick={() => setColumnConfigDiscipline('piping')}
            onRulesOfCreditClick={() => setRulesOfCreditDiscipline('piping')}
            visibleColumns={disciplineVisibleColumns['piping']}
            columnOrder={disciplineColumnOrder['piping']}
            refreshKey={matrixRefreshKey}
            onFilterStateChange={setMatrixFilterInfo}
            pipingSubViewId={pipingSubViewId}
            onPipingSubViewChange={handlePipingSubViewChange}
          />
        );
      }
      case 'civil':
      case 'civil-progress-tracker-update':
        return (
          <DisciplineMatrix
            disciplineId="civil"
            projectId={projectId}
            projectDetails={projectDetails}
            onUploadClick={() => setShowCivilUploadModal(true)}
            onExportClick={() => setExportDiscipline('civil')}
            onDownloadTemplateClick={() => handleDownloadTemplate('civil')}
            onColumnConfigClick={() => setColumnConfigDiscipline('civil')}
            onRulesOfCreditClick={() => setRulesOfCreditDiscipline('civil')}
            visibleColumns={disciplineVisibleColumns['civil']}
            columnOrder={disciplineColumnOrder['civil']}
            refreshKey={matrixRefreshKey}
            onFilterStateChange={setMatrixFilterInfo}
          />
        );
      case 'electrical':
      case 'electrical-progress-tracker-update':
        return (
          <DisciplineMatrix
            disciplineId="electrical"
            projectId={projectId}
            projectDetails={projectDetails}
            onUploadClick={() => setShowElectricalUploadModal(true)}
            onExportClick={() => setExportDiscipline('electrical')}
            onDownloadTemplateClick={() => handleDownloadTemplate('electrical')}
            onColumnConfigClick={() => setColumnConfigDiscipline('electrical')}
            onRulesOfCreditClick={() => setRulesOfCreditDiscipline('electrical')}
            visibleColumns={disciplineVisibleColumns['electrical']}
            columnOrder={disciplineColumnOrder['electrical']}
            refreshKey={matrixRefreshKey}
            onFilterStateChange={setMatrixFilterInfo}
          />
        );

      // Change Orders (CCR) Tracking
      case 'change-orders':
        return (
          <ChangeOrdersTracking
            projectId={projectId}
            jobNumber={projectDetails?.job_number}
            onUploadClick={() => setShowCCRUploadModal(true)}
            onExportClick={() => setShowCCRExportModal(true)}
            onDownloadTemplateClick={() => handleCCRDownloadTemplate()}
          />
        );

      // Change Management - Control Chart
      case 'control-chart':
        return (
          <ControlChartDashboard
            projectId={projectId}
            jobNumber={projectDetails?.job_number}
          />
        );
      

      // Column Config
      case 'mechanical-rotating-eqp-column-config':
        return <PlaceholderContent title="Mechanical - Column Config" description="Configure columns for rotating equipment tracking." icon="⚙️" />;
      case 'mechanical-ducts-column-config':
        return <PlaceholderContent title="Ducts - Column Config" description="Configure columns for ducts tracking." icon="⚙️" />;
      case 'mechanical-vessels-column-config':
        return <PlaceholderContent title="Vessels - Column Config" description="Configure columns for vessels tracking." icon="⚙️" />;
      case 'structure-column-config':
        return <PlaceholderContent title="Structural - Column Config" description="Configure columns for structural steel tracking." icon="⚙️" />;
      case 'hvac-column-config':
        return <PlaceholderContent title="HVAC - Column Config" description="Configure columns for HVAC tracking." icon="⚙️" />;

      // Rules of Credit
      case 'piping-insulation-rules-of-credit':
        return <PlaceholderContent title="Piping & Insulation - Rules of Credit" description="Manage rules of credit for piping and insulation work." icon="📋" />;
      case 'shop-field-ratio':
        return <PlaceholderContent title="Shop/Field Ratio" description="Manage shop/field ratio settings for piping and insulation work." icon="📊" />;
      case 'mechanical-rotating-eqp-rules-of-credit':
        return <PlaceholderContent title="Mechanical - Rules of Credit" description="Manage rules of credit for rotating equipment." icon="📋" />;
      case 'mechanical-ducts-rules-of-credit':
        return <PlaceholderContent title="Ducts - Rules of Credit" description="Manage rules of credit for ducts." icon="📋" />;
      case 'mechanical-vessels-rules-of-credit':
        return <PlaceholderContent title="Vessels - Rules of Credit" description="Manage rules of credit for vessels." icon="📋" />;
      case 'civil-rules-of-credit':
        return <PlaceholderContent title="Civil - Rules of Credit" description="Manage rules of credit for civil engineering work." icon="📋" />;
      case 'structure-rules-of-credit':
        return <PlaceholderContent title="Structural - Rules of Credit" description="Manage rules of credit for structural work." icon="📋" />;
      case 'electrical-rules-of-credit':
        return <PlaceholderContent title="Electrical - Rules of Credit" description="Manage rules of credit for electrical installation." icon="📋" />;
      case 'hvac-rules-of-credit':
        return <PlaceholderContent title="HVAC - Rules of Credit" description="Manage rules of credit for HVAC systems." icon="📋" />;

      // QA/QC - Fillable PDF Forms
      case 'qa-qc-fillable-concrete-inspection':
      case 'qa-qc-fillable-conveyors-feeders':
      case 'qa-qc-fillable-exhibit-b-incoming-inspection':
      case 'qa-qc-fillable-grout-inspection':
      case 'qa-qc-fillable-heat-exchangers':
      case 'qa-qc-fillable-pipe-inspection-master':
      case 'qa-qc-fillable-pumps':
      case 'qa-qc-fillable-screw-conveyors-feeders':
      case 'qa-qc-fillable-structural-steel-completion':
      case 'qa-qc-fillable-structural-steel-inspection':
      case 'qa-qc-fillable-tanks':
        return <FillableForm formId={activeMenuItem} projectDetails={projectDetails} />;

      case 'qa-qc-checklists':
        return <QAQCForms projectId={projectId} projectDetails={projectDetails} />;
      case 'qa-qc-material-receiving-inspection':
      case 'qa-qc-piping-material-receiving-checklist':
      case 'qa-qc-weld-fit-up-visual-inspection':
      case 'qa-qc-ndt-weld-examination-report':
      case 'qa-qc-piping-hydrostatic-test-report':
      case 'qa-qc-piping-pneumatic-test-report':
      case 'qa-qc-piping-pre-commissioning-checklist':
      case 'qa-qc-mechanical-completion-checklist':
      case 'qa-qc-punch-list-deficiency-list':
      case 'qa-qc-non-conformance-report':
        return <QAQCForms projectId={projectId} projectDetails={projectDetails} />;

      // Safety
      case 'safety-jha-jsa':
        return <PlaceholderContent title="Job Hazard Analysis (JHA) / JSA" description="Create and manage Job Hazard Analysis and Job Safety Analysis documents." icon="🦺" />;
      case 'safety-toolbox-talk':
        return <PlaceholderContent title="Toolbox Safety Talk Record" description="Record and track toolbox safety talks and safety briefings." icon="📢" />;
      case 'safety-daily-inspection':
        return <PlaceholderContent title="Daily Site Safety Inspection Checklist" description="Manage daily site safety inspection checklists and findings." icon="📋" />;
      case 'safety-ptw-piping-mechanical':
        return <PlaceholderContent title="Permit to Work (PTW) – Piping & Mechanical" description="Manage permits to work for piping and mechanical activities." icon="📝" />;
      case 'safety-hot-work-permit':
        return <PlaceholderContent title="Hot Work Permit" description="Track and manage hot work permits and safety requirements." icon="🔥" />;
      case 'safety-incident-near-miss':
        return <PlaceholderContent title="Incident / Near-Miss Report" description="Report and track incidents and near-miss events." icon="⚠️" />;

      case 'actuals-report':
      case 'earned-report':
        // Folded into EVM Report (Resource Control)
        return <EVMReport jobNumber={projectDetails?.job_number} />;

      case 'evm-report':
        return <EVMReport jobNumber={projectDetails?.job_number} />;

      case 'client-report':
        return <ClientReport jobNumber={projectDetails?.job_number} />;

      case 'progress-report':
        // Budget Hours Baseline is a popup on EVM; deep-link still opens EVM
        return <EVMReport jobNumber={projectDetails?.job_number} openBudgetBaseline />;

      case 'hse-report':
        // Company-wide — lives under HSE department (not project Reports)
        return <HSEReport />;

      case 'sov-report':
        return <SOVReport projectId={projectId} />;

      // Schedule
      case 'schedule-instances':
        return <ScheduleInstances jobNumber={projectDetails?.job_number} projectId={projectId} />;
      case 'schedule-planner':
        return <Planner jobNumber={projectDetails?.job_number} />;
      case 'schedule-master-components':
        return <MasterComponentList jobNumber={projectDetails?.job_number} projectId={projectId} />;
      case 'schedule-ga-dashboards':
        return (
          <GaDashboards
            jobNumber={projectDetails?.job_number}
            jobName={projectDetails?.job_name}
            projectId={projectId}
          />
        );
      case 'schedule-msp-export':
        return (
          <MspExport
            jobNumber={projectDetails?.job_number}
            jobName={projectDetails?.job_name}
          />
        );
      case 'schedule-execution-plan':
        return (
          <ExecutionPlan
            jobNumber={projectDetails?.job_number}
            jobName={projectDetails?.job_name}
          />
        );
      case 'schedule-analysis':
        return (
          <ScheduleAnalysis
            jobNumber={projectDetails?.job_number}
            jobName={projectDetails?.job_name}
            projectId={projectId}
          />
        );
      case 'schedule-modules':
        return (
          <ScheduleModulesPanel
            scopeType="job"
            scopeKey={projectDetails?.job_number}
            title="Schedule options"
          />
        );
      case 'schedule-lbs':
        return (
          <EstimateLbsOnePager
            estimateNumber={projectDetails?.estimate_number}
            jobNumber={projectDetails?.job_number}
          />
        );
      case 'schedule-model':
        return <ScheduleModel jobNumber={projectDetails?.job_number} projectId={projectId} />;
      case 'schedule-checklists':
        return <ScheduleChecklists jobNumber={projectDetails?.job_number} projectId={projectId} />;

      // Training
      case 'training-shortcuts':
        return <KeyboardShortcuts />;
      case 'training-mouse-gestures':
        return <MouseGestures />;

      default:
        return <PlaceholderContent title={activeMenuItem} description="This feature is coming soon." icon="🚧" />;
    }
  };

  // ============================================
  // Render
  // ============================================

  // Loading state
  if (loadingProjectDetails) {
    return (
      <div style={{ padding: '2rem', maxWidth: '95%', margin: '0 auto' }}>
        <div style={{
          textAlign: 'center',
          padding: '5rem',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem',
          }}></div>
          <div style={{ color: '#666', fontSize: '16px' }}>Loading project...</div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Project not found
  if (!projectDetails) {
    return (
      <div style={{ padding: '2rem', maxWidth: '95%', margin: '0 auto' }}>
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
        }}>
          <h3 style={{ color: '#721c24' }}>{/permission|failed to load|could not read/i.test(projectLoadError) ? 'Could not load project' : 'Project Not Found'}</h3>
          <p style={{ color: '#721c24' }}>{projectLoadError || `No job matched "${projectId}". Use Select Project or the job number (for example 71940).`}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#721c24',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      height: 'calc(100vh - var(--header-height, 45px))',
      overflow: 'hidden'
    }}>
      {/* Mobile Menu Toggle Button */}
      {(isMobile || isTablet) && (
        <button
          onClick={() => setMobileSidebarOpen(true)}
          aria-label="Open menu"
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(220, 53, 69, 0.4)',
            cursor: 'pointer',
            zIndex: 90,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
          }}
        >
          ☰
        </button>
      )}

      {/* Left Sidebar */}
      <ProjectSidebar
        activeMenuItem={activeMenuItem}
        onMenuItemClick={handleMenuItemClick}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
        isMobileOpen={mobileSidebarOpen}
        onMobileClose={handleMobileClose}
        projectId={projectId}
        selectedDepartment={selectedDepartment}
        jobNumber={projectDetails?.job_number || null}
      />

      {/* Main Content Area */}
      <div style={{ 
        flex: 1, 
        minWidth: 0,
        overflowY: 'auto',
        padding: (isMobile || isTablet) ? '1rem' : '1.5rem',
        backgroundColor: '#f5f7fa',
        // On mobile, take full width since sidebar is overlay
        marginLeft: (isMobile || isTablet) ? 0 : undefined,
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: (isMobile || isTablet) ? '1rem' : '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          minHeight: 'calc(100% - 3rem)'
        }}>
          {/* Project Header */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '0.5rem' : '1rem',
            marginBottom: '1rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #e9ecef'
          }}>
            <h2 style={{ 
              margin: 0, 
              color: '#333', 
              fontSize: isMobile ? '1rem' : '1.25rem',
              wordBreak: 'break-word',
            }}>
              {projectDetails.job_number} - {projectDetails.job_name}
              {activeMenuItem && activeMenuItem !== 'home' && (() => {
                // Map 'matrix' to 'piping-insulation' for label lookup
                const menuIdForLabel = activeMenuItem === 'matrix' ? 'piping-insulation' : activeMenuItem;
                const pageTitle = getMenuItemLabel(menuIdForLabel);
                return pageTitle ? (
                  <>
                    <span style={{ color: '#999', margin: '0 0.5rem' }}>|</span>
                    <span style={{ color: '#666', fontWeight: '500' }}>
                      {pageTitle}
                    </span>
                  </>
                ) : null;
              })()}
            </h2>
            
            {/* Filter Summary - shown when matrix has active filters */}
            {matrixFilterInfo?.hasActiveFilters && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '6px 12px',
                backgroundColor: '#d1fae5',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#065f46',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontWeight: 'bold' }}>Filters:</span>
                {matrixFilterInfo.searchQuery && (
                  <span style={{ 
                    padding: '2px 8px', 
                    backgroundColor: '#10b981', 
                    color: 'white', 
                    borderRadius: '4px',
                    fontSize: '11px'
                  }}>
                    Search: "{matrixFilterInfo.searchQuery}"
                  </span>
                )}
                {matrixFilterInfo.activeFilters?.map((filter, idx) => (
                  <span key={idx} style={{ 
                    padding: '2px 8px', 
                    backgroundColor: '#10b981', 
                    color: 'white', 
                    borderRadius: '4px',
                    fontSize: '11px'
                  }}>
                    {filter.columnName}: {filter.type === 'values' 
                      ? `${filter.count} values` 
                      : `${filter.mode} "${filter.value || ''}"`
                    }
                  </span>
                ))}
                <button
                  onClick={() => matrixFilterInfo.onClearAll?.()}
                  style={{
                    padding: '2px 10px',
                    backgroundColor: '#065f46',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#064e3b'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#065f46'}
                >
                  ✕ Clear All
                </button>
              </div>
            )}
          </div>

        {/* Job Status Monitors */}
        {activeJobId && (
          <JobStatusMonitor
            jobId={activeJobId}
            onComplete={handleJobComplete}
            onError={handleJobError}
          />
        )}
        {activeExportJobId && (
          <JobStatusMonitor
            jobId={activeExportJobId}
            onComplete={handleExportJobComplete}
            onError={handleExportJobError}
          />
        )}
        {/* Other discipline exports now use direct download, no job monitor needed */}

        {/* Main Content */}
        <div style={{ marginTop: '1rem' }}>
          {renderContent()}
        </div>

        {/* ============================================ */}
        {/* Modals - Conditionally Rendered for Performance */}
        {/* ============================================ */}

        {/* Piping Matrix Modals */}
        {showUploadModal && (
          <UploadMatrixModal
            isOpen={true}
            onClose={handleCloseUploadModal}
            onUploadSuccess={handleUploadSuccess}
            onUploadError={handleUploadError}
            projectId={projectId}
          />
        )}


        {showProgressModal && (
          <ProgressTrendsModal
            isOpen={true}
            onClose={handleCloseProgressModal}
            matrixId={selectedMatrixId}
            lineTagNumber={selectedLineTag}
            projectId={projectId}
          />
        )}

        {/* Old ColumnConfigModal removed - piping now uses DisciplineColumnConfigModal below */}

        {columnConfigDiscipline && (
          <DisciplineColumnConfigModal
            isOpen={true}
            onClose={handleCloseDisciplineColumnConfig}
            projectId={projectId}
            discipline={columnConfigDiscipline}
            columns={getDisciplineConfig(columnConfigDiscipline).columns.map(c => ({ key: c.id || c.accessor, header: c.header, showInDashboard: c.showInDashboard }))}
            visibleColumns={disciplineVisibleColumns[columnConfigDiscipline] || new Set()}
            columnOrder={disciplineColumnOrder[columnConfigDiscipline] || []}
            onSave={(updatedMappings) => {
              // For piping, update the columnMappings state so the matrix reflects the new UDF columns
              if (columnConfigDiscipline === 'piping') {
                setColumnMappings(updatedMappings);
              }
              // Bump refresh key so DisciplineMatrix re-fetches column mappings
              setMatrixRefreshKey(prev => prev + 1);
              showToast('Column configuration saved successfully!', 'success');
            }}
            onVisibilityChange={(newVisibleColumns) => {
              setDisciplineVisibleColumns(prev => ({
                ...prev,
                [columnConfigDiscipline]: newVisibleColumns
              }));
            }}
            onOrderChange={(order) => {
              setDisciplineColumnOrder(prev => ({
                ...prev,
                [columnConfigDiscipline]: order
              }));
            }}
          />
        )}

        {exportDiscipline && (
          <ExportOptionsModal
            isOpen={true}
            onClose={handleCloseExportModal}
            onExport={exportDisciplineData}
            onDownloadTemplate={() => handleDownloadTemplate(exportDiscipline)}
            isExporting={exportingData}
            disciplineName={exportDiscipline.charAt(0).toUpperCase() + exportDiscipline.slice(1)}
            showAllTabsOption={['mechanical', 'vessels', 'chutes', 'conveyors', 'skids'].includes(exportDiscipline)}
            filteredCount={matrixFilterInfo?.filteredCount ?? 0}
            completeCount={matrixFilterInfo?.completeCount ?? 0}
            getRowIds={matrixFilterInfo?.getRowIds}
          />
        )}

        {showMatBoreModal && (
          <MatBorePercentagesModal
            isOpen={true}
            onClose={handleCloseMatBoreModal}
            projectId={projectId}
            jobNumber={projectDetails?.job_number}
            onSaveSuccess={handleSaveSuccess}
            onSaveError={handleSaveError}
          />
        )}

        {showContractorCriteriaModal && (
          <ContractorSelectionCriteriaModal
            isOpen={true}
            onClose={handleCloseContractorCriteriaModal}
            onSaveSuccess={handleSaveSuccess}
            onSaveError={handleSaveError}
          />
        )}

        {showManageTeamModal && (
          <ProjectUserAssignmentModal
            isOpen={true}
            onClose={handleCloseManageTeamModal}
            projectId={projectId}
            projectName={projectDetails ? `${projectDetails.job_number} - ${projectDetails.job_name}` : ''}
            onSaveSuccess={handleSaveSuccess}
            onSaveError={handleSaveError}
          />
        )}

        {/* HVAC Modals */}
        {showHvacUploadModal && (
          <UploadModal
            isOpen={true}
            onClose={handleCloseHvacUpload}
            onUploadSuccess={handleHvacUploadSuccess}
            onUploadError={handleHvacUploadError}
            jobNumber={projectDetails?.job_number}
            config={UPLOAD_CONFIGS.HVAC}
          />
        )}

        {/* Rotating Equipment Modals */}
        {showRotatingEquipmentUploadModal && (
          <UploadModal
            isOpen={true}
            onClose={handleCloseRotatingEquipmentUpload}
            onUploadSuccess={handleRotatingEquipmentUploadSuccess}
            onUploadError={handleRotatingEquipmentUploadError}
            jobNumber={projectDetails?.job_number}
            config={UPLOAD_CONFIGS.ROTATING_EQUIPMENT}
          />
        )}

        {/* Ducts Modals */}
        {showDuctsUploadModal && (
          <UploadModal
            isOpen={true}
            onClose={handleCloseDuctsUpload}
            onUploadSuccess={handleDuctsUploadSuccess}
            onUploadError={handleDuctsUploadError}
            jobNumber={projectDetails?.job_number}
            config={UPLOAD_CONFIGS.DUCTS}
          />
        )}

        {/* Vessels Modals */}
        {showVesselsUploadModal && (
          <UploadModal
            isOpen={true}
            onClose={handleCloseVesselsUpload}
            onUploadSuccess={handleVesselsUploadSuccess}
            onUploadError={handleVesselsUploadError}
            jobNumber={projectDetails?.job_number}
            config={UPLOAD_CONFIGS.VESSELS}
          />
        )}

        {/* Structural Steel Modals */}
        {showStructuralSteelUploadModal && (
          <UploadModal
            isOpen={true}
            onClose={handleCloseStructuralSteelUpload}
            onUploadSuccess={handleStructuralSteelUploadSuccess}
            onUploadError={handleStructuralSteelUploadError}
            jobNumber={projectDetails?.job_number}
            config={UPLOAD_CONFIGS.STRUCTURAL_STEEL}
          />
        )}

        {/* Civil Modals */}
        {showCivilUploadModal && (
          <UploadModal
            isOpen={true}
            onClose={handleCloseCivilUpload}
            onUploadSuccess={handleCivilUploadSuccess}
            onUploadError={handleCivilUploadError}
            jobNumber={projectDetails?.job_number}
            config={UPLOAD_CONFIGS.CIVIL}
          />
        )}

        {/* Boilermaker Modals */}
        {showBoilermakerUploadModal && (
          <UploadModal
            isOpen={true}
            onClose={handleCloseBoilermakerUpload}
            onUploadSuccess={handleBoilermakerUploadSuccess}
            onUploadError={handleBoilermakerUploadError}
            jobNumber={projectDetails?.job_number}
            config={UPLOAD_CONFIGS.BOILERMAKER}
          />
        )}

        {/* Electrical Modals */}
        {showElectricalUploadModal && (
          <UploadModal
            isOpen={true}
            onClose={handleCloseElectricalUpload}
            onUploadSuccess={handleElectricalUploadSuccess}
            onUploadError={handleElectricalUploadError}
            jobNumber={projectDetails?.job_number}
            config={UPLOAD_CONFIGS.ELECTRICAL}
          />
        )}

        {/* Unified Mechanical Upload Modal (handles Rotating Equipment, Vessels, etc.) */}
        {showMechanicalUnifiedUploadModal && (
          <MechanicalUploadModal
            isOpen={true}
            onClose={() => setShowMechanicalUnifiedUploadModal(false)}
            onUploadSuccess={(result) => {
              showToast(result.message || 'Mechanical data uploaded successfully!', 'success');
              setMatrixRefreshKey(prev => prev + 1);
              setShowMechanicalUnifiedUploadModal(false);
            }}
            onUploadError={(message) => {
              showToast(message || 'Failed to upload mechanical data', 'error');
            }}
            projectId={projectId}
          />
        )}

        {/* CCR (Change Orders) Modals */}
        {showCCRUploadModal && (
          <CCRUploadModal
            isOpen={true}
            onClose={handleCloseCCRUpload}
            onUploadSuccess={handleCCRUploadSuccess}
            onUploadError={handleCCRUploadError}
            jobNumber={projectDetails?.job_number}
          />
        )}
        {showCCRExportModal && (
          <CCRExportOptionsModal
            isOpen={true}
            onClose={handleCloseCCRExport}
            onExport={handleCCRExport}
            isExporting={ccrExporting}
          />
        )}

        {/* Rules of Credit Modal */}
        {rulesOfCreditDiscipline !== null && (
          <RulesOfCreditModal
            isOpen={true}
            onClose={handleCloseRulesOfCredit}
            discipline={rulesOfCreditDiscipline}
            jobNumber={projectDetails?.job_number}
            onSave={handleRulesOfCreditSave}
          />
        )}

        {/* NDE Import Modal */}
        {showNdeImportModal && (
          <NdeImportModal
            isOpen={true}
            onClose={() => setShowNdeImportModal(false)}
            projectId={projectId}
            jobNumber={projectDetails?.job_number}
            onImportSuccess={() => {
              showToast('NDE data imported successfully!', 'success');
              setMatrixRefreshKey(prev => prev + 1);
              setShowNdeImportModal(false);
            }}
            onImportError={(message) => {
              showToast(message || 'Failed to import NDE data', 'error');
            }}
          />
        )}

        {/* NDE Audit Log Modal */}
        {showNdeAuditLogModal && (
          <NdeAuditLogModal
            isOpen={true}
            onClose={() => setShowNdeAuditLogModal(false)}
            projectId={projectId}
            jobNumber={projectDetails?.job_number}
          />
        )}

        {/* Pipe Testing Import Modal */}
        {showPipeTestingImportModal && (
          <PipeTestingImportModal
            isOpen={true}
            onClose={() => setShowPipeTestingImportModal(false)}
            projectId={projectId}
            jobNumber={projectDetails?.job_number}
            onImportSuccess={() => {
              showToast('Testing Tracker data imported successfully!', 'success');
              setMatrixRefreshKey(prev => prev + 1);
              setShowPipeTestingImportModal(false);
            }}
            onImportError={(message) => {
              showToast(message || 'Failed to import Testing Tracker data', 'error');
            }}
          />
        )}

        {/* Pipe Testing Audit Log Modal */}
        {showPipeTestingAuditLogModal && (
          <PipeTestingAuditLogModal
            isOpen={true}
            onClose={() => setShowPipeTestingAuditLogModal(false)}
            projectId={projectId}
            jobNumber={projectDetails?.job_number}
          />
        )}

        {/* Tutorial Overlay */}
        {showTutorial && (
          <Tutorial
            user={user}
            isActive={true}
            onClose={handleCloseTutorial}
          />
        )}

        {/* Toast Notification */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.show}
          onClose={closeToast}
          duration={5000}
        />
        </div>
      </div>
    </div>
  );
};

export default ProjectPage;
